import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type Folder = Database['public']['Tables']['folders']['Row'];

interface FolderManagerProps {
  darkMode: boolean;
  onFolderCreated: () => void;
  folders: Folder[];
  selectedFolder: string | null;
  onFolderSelect: (folderId: string | null) => void;
}

export function FolderManager({ 
  darkMode, 
  onFolderCreated, 
  folders, 
  selectedFolder, 
  onFolderSelect 
}: FolderManagerProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#3B82F6');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const colors = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#F97316', // Orange
    '#06B6D4', // Cyan
    '#84CC16', // Lime
  ];

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('No user ID found');

      const { error: insertError } = await supabase
        .from('folders')
        .insert([{
          user_id: userId,
          name: newFolderName.trim(),
          color: newFolderColor
        }]);

      if (insertError) throw insertError;

      setNewFolderName('');
      setNewFolderColor('#3B82F6');
      setShowCreateForm(false);
      onFolderCreated();
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!window.confirm('Are you sure you want to delete this folder? QR codes in this folder will be moved to "No Folder".')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;

      if (selectedFolder === folderId) {
        onFolderSelect(null);
      }
      onFolderCreated();
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError('Failed to delete folder');
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Folders
        </h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className={`px-3 py-1 text-sm rounded-md ${
            darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {showCreateForm ? 'Cancel' : '+ New Folder'}
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 text-red-600 rounded-md text-sm">
          {error}
        </div>
      )}

      {showCreateForm && (
        <form onSubmit={handleCreateFolder} className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <div className="space-y-3">
            <div>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className={`w-full px-3 py-2 text-sm rounded-md border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>
            <div>
              <div className="flex gap-2">
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewFolderColor(color)}
                    className={`w-6 h-6 rounded-full border-2 ${
                      newFolderColor === color ? 'border-gray-800' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className={`px-3 py-1 text-sm rounded-md ${
                  darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onFolderSelect(null)}
          className={`px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
            selectedFolder === null
              ? darkMode
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-800'
              : darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📁 All QR Codes
        </button>
        
        {folders.map((folder) => (
          <div key={folder.id} className="relative group">
            <button
              onClick={() => onFolderSelect(folder.id)}
              className={`px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
                selectedFolder === folder.id
                  ? darkMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-800'
                  : darkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: folder.color }}
              />
              {folder.name}
            </button>
            <button
              onClick={() => handleDeleteFolder(folder.id)}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}